/**
 * Suomi-Seura Theme JavaScript
 *
 * @package Suomi-Seura
 */

// Import Swiper
import Swiper from 'swiper';
import { Navigation, Pagination, EffectFade, Autoplay } from 'swiper/modules';

document.addEventListener('DOMContentLoaded', function() {

    /**
     * Mobile Menu Handler
     * Handles mobile menu toggle functionality
     */
    class MobileMenuHandler {
        constructor() {
            this.menuTrigger = document.querySelector('.site-header .menu-trigger');
            this.mobileMenuPanel = document.querySelector('.site-header .mobile-menu-panel');
            this.body = document.body;
            this.isOpen = false;

            this.init();
        }

        init() {
            if (!this.menuTrigger || !this.mobileMenuPanel) {
                console.warn('Mobile menu elements not found');
                return;
            }

            this.bindEvents();
        }

        bindEvents() {
            // Toggle menu on trigger click
            this.menuTrigger.addEventListener('click', this.toggleMenu.bind(this));

            // Close menu when clicking outside
            document.addEventListener('click', this.handleOutsideClick.bind(this));

            // Close menu on escape key
            document.addEventListener('keydown', this.handleKeydown.bind(this));

            // Close menu on window resize (mobile to desktop)
            window.addEventListener('resize', this.handleResize.bind(this));
        }

        toggleMenu(event) {
            event.preventDefault();
            event.stopPropagation();

            this.isOpen = !this.isOpen;

            if (this.isOpen) {
                this.openMenu();
            } else {
                this.closeMenu();
            }
        }

        openMenu() {
            this.mobileMenuPanel.classList.add('shown');
            this.menuTrigger.classList.add('is-active');
            this.body.classList.add('overflow-y-hidden');

            // Set ARIA attributes for accessibility
            this.menuTrigger.setAttribute('aria-expanded', 'true');
            this.mobileMenuPanel.setAttribute('aria-hidden', 'false');

            // Focus management for accessibility
            const firstFocusableElement = this.mobileMenuPanel.querySelector('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (firstFocusableElement) {
                firstFocusableElement.focus();
            }
        }

        closeMenu() {
            // Move focus out of panel before hiding (avoid aria-hidden on focused element's ancestor)
            if (this.mobileMenuPanel.contains(document.activeElement)) {
                this.menuTrigger.focus();
            }

            this.mobileMenuPanel.classList.remove('shown');
            this.menuTrigger.classList.remove('is-active');
            this.body.classList.remove('overflow-y-hidden');

            this.menuTrigger.setAttribute('aria-expanded', 'false');
            this.mobileMenuPanel.setAttribute('aria-hidden', 'true');
        }

        handleOutsideClick(event) {
            if (this.isOpen &&
                !this.mobileMenuPanel.contains(event.target) &&
                !this.menuTrigger.contains(event.target)) {
                this.closeMenu();
            }
        }

        handleKeydown(event) {
            if (event.key === 'Escape' && this.isOpen) {
                this.closeMenu();
            }
        }

        handleResize() {
            // Close menu when switching to desktop view
            if (window.innerWidth >= 1280 && this.isOpen) {
                this.closeMenu();
            }
        }
    }

    /**
     * Smooth Scroll Handler
     * Handles smooth scrolling for anchor links
     */
    class SmoothScrollHandler {
        constructor() {
            this.init();
        }

        init() {
            const anchorLinks = document.querySelectorAll('a[href^="#"]');
            anchorLinks.forEach(link => {
                link.addEventListener('click', this.handleAnchorClick.bind(this));
            });
        }

        handleAnchorClick(event) {
            const link = event.currentTarget;
            const href = link.getAttribute('href');

            // Skip if it's just "#"
            if (href === '#') return;

            // Skip-link is handled by AccessibilityHandler
            if (link.classList.contains('skip-link')) return;

            const targetElement = document.querySelector(href);
            if (!targetElement) return;

            event.preventDefault();

            const headerHeight = document.querySelector('.site-header')?.offsetHeight || 0;
            const targetPosition = targetElement.offsetTop - headerHeight - 20;

            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });
        }
    }

    /**
     * Lazy Loading Handler
     * Handles lazy loading for images
     */
    class LazyLoadingHandler {
        constructor() {
            this.init();
        }

        init() {
            if ('IntersectionObserver' in window) {
                this.observeImages();
            } else {
                // Fallback for older browsers
                this.loadAllImages();
            }
        }

        observeImages() {
            const imageObserver = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        this.loadImage(img);
                        observer.unobserve(img);
                    }
                });
            });

            const lazyImages = document.querySelectorAll('img[data-src]');
            lazyImages.forEach(img => imageObserver.observe(img));
        }

        loadImage(img) {
            img.src = img.dataset.src;
            img.classList.remove('lazy');
            img.classList.add('loaded');
        }

        loadAllImages() {
            const lazyImages = document.querySelectorAll('img[data-src]');
            lazyImages.forEach(img => this.loadImage(img));
        }
    }

    /**
     * Form Enhancement Handler
     * Enhances form functionality
     */
    class FormEnhancementHandler {
        constructor() {
            this.init();
        }

        init() {
            const forms = document.querySelectorAll('form');
            forms.forEach(form => {
                this.enhanceForm(form);
            });
        }

        enhanceForm(form) {
            // Skip WordPress comment form - it has its own handling
            if (form.id === 'commentform') {
                return;
            }

            // Add loading state to submit buttons
            const submitButtons = form.querySelectorAll('input[type="submit"], button[type="submit"]');
            submitButtons.forEach(button => {
                button.addEventListener('click', () => {
                    if (form.checkValidity()) {
                        button.classList.add('loading');
                        button.disabled = true;
                    }
                });
            });

            // Real-time validation
            const inputs = form.querySelectorAll('input, textarea, select');
            inputs.forEach(input => {
                input.addEventListener('blur', () => this.validateField(input));
                input.addEventListener('input', () => this.clearFieldError(input));
            });
        }

        validateField(field) {
            const isValid = field.checkValidity();
            const fieldContainer = field.closest('.field, .form-group, p');

            if (fieldContainer) {
                if (isValid) {
                    fieldContainer.classList.remove('error');
                    fieldContainer.classList.add('valid');
                } else {
                    fieldContainer.classList.add('error');
                    fieldContainer.classList.remove('valid');
                }
            }
        }

        clearFieldError(field) {
            const fieldContainer = field.closest('.field, .form-group, p');
            if (fieldContainer) {
                fieldContainer.classList.remove('error');
            }
        }
    }

    /**
     * Swiper Handler
     * Handles Swiper slider initialization
     */
    class SwiperHandler {
        constructor() {
            this.swipers = [];
            this.init();
        }

        init() {
            this.initHeroSliders();
            this.initSectionSliders();
        }

        initHeroSliders() {
            const heroSliders = document.querySelectorAll('.swiper-hero');
            heroSliders.forEach((slider, index) => {
              slider.classList.add(`swiper-hero-${index}`);

                const paginationEl = slider.querySelector('.swiper-pagination');
                
                const swiperConfig = {
                    loop: true,
                    effect: 'fade',
                    fadeEffect: {
                        crossFade: true
                    },
                    autoplay: {
                        delay: 4000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                    },
                    modules: [EffectFade, Pagination, Autoplay]
                };

                if (paginationEl) {
                    swiperConfig.pagination = {
                        el: paginationEl,
                        clickable: true,
                    };
                }

                const swiper = new Swiper(slider, swiperConfig);
                this.swipers.push(swiper);
            });
        }

        initSectionSliders() {
          const sectionSliders = document.querySelectorAll('.swiper-section');
          sectionSliders.forEach((slider, index) => {
            slider.classList.add(`swiper-section-${index}`);

                const swiperConfig = {
              loop: false,
              effect: 'fade',
              fadeEffect: {
                crossFade: true
              },
              navigation: {
                nextEl: `.swiper-section-${index} .swiper-navigation .swiper-button-next`,
                prevEl: `.swiper-section-${index} .swiper-navigation .swiper-button-prev`,
              },
                    modules: [EffectFade, Navigation]
                };

                const swiper = new Swiper(slider, swiperConfig);
            this.swipers.push(swiper);
          });
        }

        // Method to destroy all swipers
        destroyAll() {
            this.swipers.forEach(swiper => {
                if (swiper && typeof swiper.destroy === 'function') {
                    swiper.destroy(true, true);
                }
            });
            this.swipers = [];
        }

        // Method to reinitialize swipers
        reinit() {
            this.destroyAll();
            this.init();
        }
    }

    /**
     * Accessibility Handler
     * Enhances accessibility features
     */
    class AccessibilityHandler {
        constructor() {
            this.init();
        }

        init() {
            this.addSkipLinks();
            this.enhanceFocusManagement();
            this.addAriaLabels();
        }

        addSkipLinks() {
            const skipLink = document.querySelector('.skip-link');
            if (!skipLink) return;

            const targetId = skipLink.getAttribute('href');

            const scrollToMain = () => {
                const target = document.querySelector(targetId);
                if (!target) return;

                const header = document.querySelector('.site-header');
                const headerHeight = header ? header.offsetHeight : 0;
                const computedStyle = window.getComputedStyle(target);
                const paddingTop = parseInt(computedStyle.paddingTop, 10) || 0;
                const targetTop = target.offsetTop + paddingTop - headerHeight;

                window.scrollTo({
                    top: Math.max(0, targetTop),
                    behavior: 'smooth'
                });

                history.pushState(null, null, targetId);

                target.setAttribute('tabindex', '-1');
                target.focus({ preventScroll: true });
                setTimeout(() => target.removeAttribute('tabindex'), 1000);
            };

            skipLink.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                scrollToMain();
            });

            skipLink.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    scrollToMain();
                }
            });
        }

        enhanceFocusManagement() {
            // Add focus indicators
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Tab') {
                    document.body.classList.add('keyboard-navigation');
                }
            });

            document.addEventListener('mousedown', () => {
                document.body.classList.remove('keyboard-navigation');
            });
        }

        addAriaLabels() {
            // Add ARIA labels to menu trigger
            const menuTrigger = document.querySelector('.menu-trigger');
            if (menuTrigger && !menuTrigger.getAttribute('aria-label')) {
                menuTrigger.setAttribute('aria-label', 'Toggle mobile menu');
                menuTrigger.setAttribute('aria-expanded', 'false');
            }

            // ARIA: label on nav, visibility on panel (do not set aria-hidden on nav — focus can be inside it)
            const primaryMenuNav = document.querySelector('.primary-menu');
            if (primaryMenuNav && !primaryMenuNav.getAttribute('aria-label')) {
                primaryMenuNav.setAttribute('aria-label', 'Main navigation');
            }
            const panel = document.querySelector('.mobile-menu-panel');
            if (panel) {
                panel.setAttribute('aria-hidden', panel.classList.contains('shown') ? 'false' : 'true');
            }
        }
    }

    /**
     * Desktop Menu Handler
     * Handles hover effects for desktop menu
     */
    class DesktopMenuHandler {
        constructor() {
            this.menuItems = document.querySelectorAll('.site-header .primary-menu .menu > .menu-item-has-children');
            this.overlay = document.querySelector('.site-main > .bg-overlay');

            this.init();
        }

        init() {
            if (!this.menuItems.length || !this.overlay) {
                return;
            }

            this.bindEvents();
        }

        bindEvents() {
            this.menuItems.forEach(item => {
                item.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
                item.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
            });

            window.addEventListener('resize', this.handleResize.bind(this));
        }

        handleMouseEnter() {
            if (window.innerWidth >= 1280) {
                this.overlay.classList.add('shown');
            }
        }

        handleMouseLeave() {
            this.overlay.classList.remove('shown');
        }

        handleResize() {
            if (window.innerWidth < 1280) {
                this.overlay.classList.remove('shown');
            }
        }
    }

    // Initialize all handlers
    new MobileMenuHandler();
    new DesktopMenuHandler();
    new SmoothScrollHandler();
    new LazyLoadingHandler();
    new FormEnhancementHandler();
    const swiperHandler = new SwiperHandler();
    new AccessibilityHandler();

    // Utility function for debouncing
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    // Utility function for throttling
    function throttle(func, limit) {
        let inThrottle;
        return function() {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }

    // Make utilities globally available
    window.SuomiSeuraUtils = {
        debounce,
        throttle,
        swiperHandler
    };

    // Handle dynamic content (AJAX, etc.)
    document.addEventListener('swiper-content-loaded', function() {
        if (window.SuomiSeuraUtils.swiperHandler) {
            window.SuomiSeuraUtils.swiperHandler.reinit();
        }
    });



    // Primary Menu
    const primaryMenu = document.querySelector('.primary-menu .menu');
    let isMenuInitialized = false;
    let isKeyboardNavInitialized = false;

    function initPrimaryMenu() {
        // Always initialize keyboard navigation (works at all screen sizes, including 1024px+)
        initKeyboardNavigation();

        // Check window width for click handlers
        if (window.innerWidth >= 1280) {
            if (isMenuInitialized) {
                // Remove click handlers on desktop
                primaryMenu.removeEventListener('click', handleMenuClick);
                isMenuInitialized = false;
            }
            return;
        }

        // Initialize click handlers for mobile/tablet (< 1280px)
        if (primaryMenu && !isMenuInitialized) {
            primaryMenu.addEventListener('click', handleMenuClick);
            isMenuInitialized = true;
        }
    }

    function handleMenuClick(event) {
        const menuItem = event.target.closest('.menu-item-has-children');

        if (menuItem && menuItem.parentElement === primaryMenu) {
            if (menuItem.classList.contains('active')) {
                // Check if click is on ::after pseudo-element
                const link = menuItem.querySelector('a');
                if (link) {
                    const clickX = event.clientX;
                    const linkRect = link.getBoundingClientRect();
                    const arrowArea = {
                        left: linkRect.right - 25, // Arrow starts 25px from the right edge
                        right: linkRect.right
                    };

                    const isClickOnArrow = clickX >= arrowArea.left && clickX <= arrowArea.right;

                    if (isClickOnArrow) {
                        event.preventDefault();
                        menuItem.querySelector('.sub-menu').classList.remove('shown');
                        menuItem.classList.remove('active');
                    } else {
                        const link = menuItem.querySelector('a');
                        if (link) {
                            const href = link.getAttribute('href');
                            if (href && href !== '#') {
                                window.location.href = href;
                            }
                        }
                    }
                }
            } else {
                event.preventDefault();

                // Select direct menu items
                const menuItems = Array.from(primaryMenu.children).filter(
                    child => child.classList.contains('menu-item')
                );

                // Close all sub-menus
                menuItems.forEach(menuItem => {
                    const subMenu = menuItem.querySelector('.sub-menu');
                    if (subMenu) {
                        subMenu.classList.remove('shown');
                    }
                    menuItem.classList.remove('active');
                });

                // Open current parent sub-menu
                setTimeout(() => {
                    menuItem.querySelector(':scope > .sub-menu').classList.add('shown');
                    menuItem.classList.add('active');
                }, 100);
            }
        }
    }

    const menuNavDebug = typeof window !== 'undefined' && (
        window.MENU_NAV_DEBUG === true ||
        (typeof URLSearchParams !== 'undefined' && new URLSearchParams(window.location.search).get('menu_debug') === '1')
    );
    function menuNavLog(...args) {
        if (menuNavDebug) {
            console.log('[Menu Nav Tab]', ...args);
        }
    }

    function initKeyboardNavigation() {
        if (!primaryMenu) {
            menuNavLog('init: primaryMenu not found');
            return;
        }
        const subLinks = primaryMenu.querySelectorAll('.sub-menu a');
        menuNavLog('init: primaryMenu ok, submenu links count', subLinks.length);

        // Keep submenu links out of tab order until their submenu is open
        subLinks.forEach(a => a.setAttribute('tabindex', '-1'));

        // Remove existing listeners if reinitializing
        if (isKeyboardNavInitialized) {
            const allMenuLinks = primaryMenu.querySelectorAll('a');
            allMenuLinks.forEach(link => {
                link.removeEventListener('keydown', handleMenuKeydown);
            });
        }

        // Add keyboard event listeners to all menu links (including nested)
        // This ensures keyboard navigation works at all screen sizes, including 1024px+
        const menuLinks = primaryMenu.querySelectorAll('a');
        menuLinks.forEach(link => {
            link.addEventListener('keydown', handleMenuKeydown);
        });

        isKeyboardNavInitialized = true;
    }

    function focusFirstSecondaryMenuLink() {
        const firstSecondary = document.querySelector('.site-header .secondary-menu .menu a');
        menuNavLog('focusFirstSecondaryMenuLink', { found: !!firstSecondary, href: firstSecondary ? firstSecondary.getAttribute('href') : null });
        if (firstSecondary) {
            firstSecondary.classList.add('keyboard-focus');
            firstSecondary.addEventListener('blur', function removeKeyboardFocus() {
                firstSecondary.classList.remove('keyboard-focus');
                firstSecondary.removeEventListener('blur', removeKeyboardFocus);
            }, { once: true });
            firstSecondary.focus();
        } else {
            menuNavLog('warn: .site-header .secondary-menu .menu a not found');
        }
    }

    function handleMenuKeydown(event) {
        const link = event.currentTarget;
        const menuItem = link.closest('.menu-item-has-children');
        const key = event.key;

        // Tab: open submenu and focus first item, or close submenu and focus next sibling, or leave primary for secondary
        if (key === 'Tab' && !event.shiftKey) {
            const currentSubMenu = link.closest('.sub-menu');
            const topLevelItem = link.closest('.menu-item');
            const isTopLevelLink = topLevelItem && topLevelItem.parentElement === primaryMenu;
            const isLastTopLevelItem = isTopLevelLink && !topLevelItem.nextElementSibling;
            const isParentLink = menuItem && link.parentElement === menuItem && menuItem.parentElement === primaryMenu;

            menuNavLog('Tab', {
                href: link.getAttribute('href'),
                isTopLevelLink,
                isLastTopLevelItem,
                hasChildren: !!menuItem,
                isParentLink,
                submenuOpen: menuItem ? menuItem.classList.contains('active') : false,
                inSubMenu: !!currentSubMenu,
                parentElementTag: link.parentElement ? link.parentElement.tagName : null,
                parentElementClass: link.parentElement ? link.parentElement.className : null,
                primaryMenuExists: !!primaryMenu,
                topLevelParentOk: topLevelItem && topLevelItem.parentElement === primaryMenu
            });

            // Last top-level item with no children: Tab should leave primary and go to secondary
            if (isTopLevelLink && isLastTopLevelItem && !topLevelItem.classList.contains('menu-item-has-children')) {
                menuNavLog('branch: last top-level no children -> focus secondary');
                event.preventDefault();
                focusFirstSecondaryMenuLink();
                return;
            }

            if (isParentLink && !menuItem.classList.contains('active')) {
                const subMenu = menuItem.querySelector(':scope > .sub-menu');
                menuNavLog('branch: parent link, submenu closed', { hasSubMenu: !!subMenu });
                if (subMenu) {
                    event.preventDefault();
                    openSubmenu(menuItem);
                    const firstLink = subMenu.querySelector('a');
                    if (firstLink) {
                        menuNavLog('opening submenu, focusing first link', firstLink.getAttribute('href'));
                        setTimeout(() => firstLink.focus(), 100);
                    } else {
                        menuNavLog('warn: no first link in submenu');
                    }
                }
                return;
            }

            if (currentSubMenu) {
                const directItems = Array.from(currentSubMenu.querySelectorAll(':scope > .menu-item'));
                const directLinks = directItems.map(item => item.querySelector('a')).filter(Boolean);
                const isLastInSubmenu = directLinks.length > 0 && directLinks[directLinks.length - 1] === link;

                menuNavLog('inside submenu', {
                    directItemsCount: directItems.length,
                    directLinksCount: directLinks.length,
                    isLastInSubmenu,
                    lastLinkHref: directLinks.length ? directLinks[directLinks.length - 1].getAttribute('href') : null
                });

                if (isLastInSubmenu) {
                    event.preventDefault();
                    const parentMenuItem = currentSubMenu.closest('.menu-item-has-children');
                    if (parentMenuItem) {
                        closeSubmenu(parentMenuItem);
                        const nextSibling = parentMenuItem.nextElementSibling;
                        menuNavLog('last in submenu: closed, nextSibling', nextSibling ? nextSibling.className : null);
                        if (nextSibling && nextSibling.classList.contains('menu-item')) {
                            const nextLink = nextSibling.querySelector(':scope > a') || nextSibling.querySelector('a');
                            const nextHasChildren = nextSibling.classList.contains('menu-item-has-children');
                            const nextSub = nextSibling.querySelector(':scope > .sub-menu');
                            const firstInNextSub = nextSub ? nextSub.querySelector('a') : null;
                            // <a> without href is not in tab order and may not accept focus; open its submenu and focus first link
                            if (nextLink && (!nextLink.getAttribute('href') || nextLink.getAttribute('href') === '#') && nextHasChildren && firstInNextSub) {
                                menuNavLog('next sibling link has no href, opening its submenu and focusing first link');
                                openSubmenu(nextSibling);
                                setTimeout(() => firstInNextSub.focus(), 120);
                            } else if (nextLink) {
                                menuNavLog('focusing next sibling link', nextLink.getAttribute('href'));
                                nextLink.focus();
                            } else {
                                menuNavLog('warn: next sibling has no link, calling focusFirstSecondaryMenuLink');
                                focusFirstSecondaryMenuLink();
                            }
                        } else {
                            menuNavLog('no next sibling -> focusFirstSecondaryMenuLink()');
                            focusFirstSecondaryMenuLink();
                        }
                    } else {
                        menuNavLog('warn: no parentMenuItem for currentSubMenu');
                    }
                }
                return;
            }
            menuNavLog('Tab: no branch matched, default behaviour');
        }

        // Handle keyboard navigation for items with children
        if (menuItem) {
            const subMenu = menuItem.querySelector(':scope > .sub-menu');
            if (!subMenu) {
                return;
            }

            const isTopLevel = menuItem.parentElement === primaryMenu;

            // Enter or Space to toggle submenu
            if (key === 'Enter' || key === ' ') {
                event.preventDefault();
                
                if (menuItem.classList.contains('active')) {
                    // Close submenu
                    closeSubmenu(menuItem);
                } else {
                    // Open submenu
                    if (isTopLevel) {
                        openSubmenu(menuItem);
                    } else {
                        // For nested menus, just toggle
                        subMenu.classList.add('shown');
                        menuItem.classList.add('active');
                    }
                }
                return;
            }

            // Escape to close submenu
            if (key === 'Escape') {
                if (menuItem.classList.contains('active')) {
                    event.preventDefault();
                    closeSubmenu(menuItem);
                    link.focus();
                }
                return;
            }

            // ArrowDown or ArrowRight to open submenu and focus first item
            if (key === 'ArrowDown' || key === 'ArrowRight') {
                event.preventDefault();
                
                if (!menuItem.classList.contains('active')) {
                    if (isTopLevel) {
                        openSubmenu(menuItem);
                    } else {
                        subMenu.classList.add('shown');
                        menuItem.classList.add('active');
                    }
                }
                
                // Focus first link in submenu
                const firstSubmenuLink = subMenu.querySelector('a');
                if (firstSubmenuLink) {
                    setTimeout(() => firstSubmenuLink.focus(), 100);
                }
                return;
            }

            // ArrowUp or ArrowLeft to close submenu (when on parent link)
            if (key === 'ArrowUp' || key === 'ArrowLeft') {
                if (menuItem.classList.contains('active')) {
                    event.preventDefault();
                    closeSubmenu(menuItem);
                }
                return;
            }
        }

        // Handle navigation within submenus
        const currentSubMenu = link.closest('.sub-menu');
        if (currentSubMenu) {
            const subMenuItems = Array.from(currentSubMenu.querySelectorAll('.menu-item'));
            const currentIndex = subMenuItems.findIndex(item => item.contains(link));

            if (key === 'ArrowDown') {
                event.preventDefault();
                const nextIndex = (currentIndex + 1) % subMenuItems.length;
                const nextLink = subMenuItems[nextIndex].querySelector('a');
                if (nextLink) {
                    nextLink.focus();
                }
                return;
            }

            if (key === 'ArrowUp') {
                event.preventDefault();
                const prevIndex = currentIndex === 0 ? subMenuItems.length - 1 : currentIndex - 1;
                const prevLink = subMenuItems[prevIndex].querySelector('a');
                if (prevLink) {
                    prevLink.focus();
                }
                return;
            }

            // ArrowLeft to go back to parent
            if (key === 'ArrowLeft') {
                event.preventDefault();
                const parentMenuItem = currentSubMenu.closest('.menu-item-has-children');
                if (parentMenuItem) {
                    const parentLink = parentMenuItem.querySelector('> a');
                    if (parentLink) {
                        closeSubmenu(parentMenuItem);
                        parentLink.focus();
                    }
                }
                return;
            }
        }
    }

    function openSubmenu(menuItem) {
        // Select direct menu items
        const menuItems = Array.from(primaryMenu.children).filter(
            child => child.classList.contains('menu-item')
        );

        // Close all sub-menus and remove submenu links from tab order
        menuItems.forEach(item => {
            const subMenu = item.querySelector('.sub-menu');
            if (subMenu) {
                subMenu.classList.remove('shown');
                subMenu.querySelectorAll('a').forEach(a => a.setAttribute('tabindex', '-1'));
            }
            item.classList.remove('active');
        });

        // Open current parent sub-menu and add its links to tab order
        const openSub = menuItem.querySelector(':scope > .sub-menu');
        setTimeout(() => {
            openSub.classList.add('shown');
            menuItem.classList.add('active');
            openSub.querySelectorAll('a').forEach(a => a.setAttribute('tabindex', '0'));
        }, 100);
    }

    function closeSubmenu(menuItem) {
        const subMenu = menuItem.querySelector(':scope > .sub-menu');
        if (subMenu) {
            subMenu.classList.remove('shown');
            subMenu.querySelectorAll('a').forEach(a => a.setAttribute('tabindex', '-1'));
        }
        menuItem.classList.remove('active');
    }

    // Initialize on load
    initPrimaryMenu();

    // Initialize on window resize
    window.addEventListener('resize', () => {
        initPrimaryMenu();
    });

    /**
     * Social Share Copy Link Handler
     * Handles copying link to clipboard for Instagram sharing
     */
    const copyLinkButtons = document.querySelectorAll('.copy-link-btn');
    
    copyLinkButtons.forEach(button => {
        button.addEventListener('click', async function(event) {
            event.preventDefault();
            
            const url = this.getAttribute('data-url');
            const notification = this.closest('.social-share-icons').querySelector('.copy-notification');
            
            try {
                // Try modern Clipboard API first
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(url);
                } else {
                    // Fallback for older browsers
                    const textArea = document.createElement('textarea');
                    textArea.value = url;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    textArea.style.top = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    document.execCommand('copy');
                    textArea.remove();
                }
                
                // Show notification
                if (notification) {
                    notification.classList.remove('hidden');
                    setTimeout(() => {
                        notification.classList.add('hidden');
                    }, 3000);
                }
            } catch (err) {
                console.error('Failed to copy link:', err);
            }
        });
    });

});
